/* @path: /src/pages/shelbula/workspaces/apiManager.jsx */
import { authManager } from '@/managers/AuthManager';
import { config } from '@/utils/setProdOrDev';
import { normalizeWorkspaceContextId } from '@/utils/workspaceContext';

const CHAT_API_URL = config.chatApiUrl;

const createUnsupportedError = (message) => {
    const error = new Error(message);
    error.status = 501;
    return error;
};

const handleResponse = async (response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        const error = new Error(errorData.error || errorData.message || 'An API error occurred.');
        error.status = response.status;
        error.payload = errorData;
        throw error;
    }

    if (response.status === 204) {
        return {};
    }

    const text = await response.text();
    if (!text) {
        return {};
    }

    try {
        return JSON.parse(text);
    } catch {
        throw new Error('API returned an invalid JSON response.');
    }
};

const getIdToken = async () => {
    const user = authManager.getState().user;
    if (!user) {
        throw new Error('User not authenticated.');
    }
    return user.getIdToken();
};

const fetchWorkspaceApi = async (endpoint, options = {}, timeoutMs = 0) => {
    const abortController = timeoutMs > 0 ? new AbortController() : null;
    const timeoutId = abortController
        ? setTimeout(() => abortController.abort(), timeoutMs)
        : null;

    try {
        const idToken = await getIdToken();
        const response = await fetch(`${CHAT_API_URL}${endpoint}`, {
            ...options,
            signal: options.signal || abortController?.signal,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${idToken}`,
            },
        });
        return handleResponse(response);
    } catch (error) {
        console.error(`[apiManager] Fetch failed for endpoint: ${endpoint}`, error);

        const isAbortTimeout = error?.name === 'AbortError' && timeoutMs > 0;
        if (isAbortTimeout) {
            const timeoutError = new Error(`Request timed out after ${Math.ceil(timeoutMs / 1000)}s.`);
            timeoutError.status = 408;
            timeoutError.cause = error;
            throw timeoutError;
        }

        const isNetworkFailure =
            error instanceof TypeError
            && /failed to fetch|networkerror|load failed/i.test(String(error.message || ''));

        if (isNetworkFailure) {
            const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
            const wrappedError = new Error(
                isOffline
                    ? 'Network is offline. Reconnect and try again.'
                    : `Unable to reach workspace API (${CHAT_API_URL}${endpoint}). Check backend health, API URL, and CORS settings.`
            );
            wrappedError.status = 0;
            wrappedError.cause = error;
            throw wrappedError;
        }

        throw error;
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
};

const getWorkspaceFilenameFromDisposition = (contentDisposition, fallbackName) => {
    if (typeof contentDisposition !== 'string' || !contentDisposition.trim()) {
        return fallbackName;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            return utf8Match[1];
        }
    }

    const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return basicMatch?.[1] || fallbackName;
};

const getWorkspace = (endpoint) => fetchWorkspaceApi(endpoint, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
const postWorkspace = (endpoint, body) => fetchWorkspaceApi(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const patchWorkspace = (endpoint, body) => fetchWorkspaceApi(endpoint, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const rejectUnsupported = async (message) => {
    throw createUnsupportedError(message);
};

const streamSseResponse = async (response, onEvent, signal) => {
    if (!response.body) {
        throw new Error('Streaming response body was empty.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        if (signal?.aborted) {
            await reader.cancel().catch(() => null);
            return;
        }

        const { done, value } = await reader.read();
        if (done) {
            break;
        }

        buffer += decoder.decode(value, { stream: true });

        while (true) {
            const eventBoundary = buffer.indexOf('\n\n');
            if (eventBoundary < 0) {
                break;
            }

            const rawEvent = buffer.slice(0, eventBoundary);
            buffer = buffer.slice(eventBoundary + 2);

            const dataLines = rawEvent
                .split(/\r?\n/)
                .filter((line) => line.startsWith('data:'))
                .map((line) => line.slice(5).trimStart());

            if (dataLines.length === 0) {
                continue;
            }

            onEvent(JSON.parse(dataLines.join('\n')));
        }
    }
};

export const apiManager = {
    getValidAccessToken: getIdToken,

    createWorkspace: (workspaceName) => postWorkspace('/workspaces', { workspaceName }),
    listAllWorkspacesWithDetails: () => getWorkspace('/workspaces'),
    deleteWorkspace: (workspaceId) => postWorkspace(`/workspaces/${workspaceId}/delete`, {}),
    updateWorkspaceDetails: (_userId, workspaceId, details) => patchWorkspace(`/workspaces/${workspaceId}`, details),
    regenerateWorkspaceSummary: (workspaceId, summaryOptions = {}) => {
        const normalizedWorkspaceId = normalizeWorkspaceContextId(workspaceId);
        return postWorkspace(`/workspaces/${normalizedWorkspaceId}/summary/regenerate`, summaryOptions);
    },

    getDeadCodeTOC: (workspaceId) => postWorkspace(`/workspaces/${workspaceId}/analysis/dead-code/toc`, {}),
    generateStyleGuideReport: (workspaceId, options = {}) => postWorkspace(`/workspaces/${workspaceId}/analysis/style-guide/report`, options),
    getDataStoryTOC: (workspaceId) => postWorkspace(`/workspaces/${workspaceId}/analysis/data-flow/toc`, {}),
    getBlastRadiusTOC: (workspaceId) => postWorkspace(`/workspaces/${workspaceId}/analysis/blast-radius/toc`, {}),
    generateExternalDependencyReport: (workspaceId) => postWorkspace(`/workspaces/${workspaceId}/analysis/external-dependencies/report`, {}),
    getLocalBlastRadiusGraph: (workspaceId, fileId) => postWorkspace(`/workspaces/${workspaceId}/analysis/blast-radius/local-graph`, { fileId }),
    assessImpact: (workspaceId, entityId) => postWorkspace(`/workspaces/${workspaceId}/analysis/blast-radius/assess-impact`, { entityId }),
    getFileComposition: (workspaceId, fileId) => postWorkspace(`/workspaces/${workspaceId}/analysis/blast-radius/file-composition`, { fileId }),
    getUsageTrace: (workspaceId, symbolId) => postWorkspace(`/workspaces/${workspaceId}/analysis/get-usage-trace`, { symbolId }),
    getSemanticEngineReport: (workspaceId) => getWorkspace(`/workspaces/${workspaceId}/analysis/semantic-engine/report`),
    runFullAuto: (workspaceId, conversationId, mode = 'sequential') => postWorkspace(`/workspaces/${workspaceId}/run-full-auto`, { conversationId, mode }),
    getAgentConflicts: (workspaceId, jobId) => getWorkspace(`/workspaces/${workspaceId}/agent-conflicts/${jobId}`),
    resolveAgentConflict: (workspaceId, jobId, agentId, filePath, choice, branchName) => postWorkspace(`/workspaces/${workspaceId}/agent-conflicts/${jobId}/resolve`, { agentId, filePath, choice, branchName }),
    delegateTask: () => rejectUnsupported('Native delegated workspace execution is not implemented yet.'),
    approvePlan: () => rejectUnsupported('Native delegated workspace approval is not implemented yet.'),
    denyPlan: () => rejectUnsupported('Native delegated workspace approval is not implemented yet.'),
    initiateDocumentation: (workspaceId, prompt) => {
        return rejectUnsupported('Native documentation task execution is not implemented yet.');
    },

    initiateRefactor: (workspaceId, filePath, refactorType = 'REFACTOR_SPLIT') => {
        return rejectUnsupported(`Native ${refactorType.toLowerCase()} execution is not implemented yet for ${filePath}.`);
    },

    listAllFiles: (workspaceId) => {
        const normalizedWorkspaceId = normalizeWorkspaceContextId(workspaceId);
        return getWorkspace(`/workspaces/${normalizedWorkspaceId}/files/tree`);
    },
    listDirectory: (workspaceId, directoryPath = '') => {
        const normalizedWorkspaceId = normalizeWorkspaceContextId(workspaceId);
        return postWorkspace(`/workspaces/${normalizedWorkspaceId}/files/list`, { directoryPath });
    },
    createDirectory: (workspaceId, directoryPath) => postWorkspace(`/workspaces/${workspaceId}/files/create-directory`, { directoryPath }),
    createFile: (workspaceId, filePath) => {
        const ext = (filePath.split('.').pop() || '').toLowerCase();
        const name = filePath.split('/').pop();
        const commentMap = {
            js: `// ${name}\n`, jsx: `// ${name}\n`, ts: `// ${name}\n`, tsx: `// ${name}\n`,
            mjs: `// ${name}\n`, cjs: `// ${name}\n`,
            css: `/* ${name} */\n`, scss: `/* ${name} */\n`, less: `/* ${name} */\n`,
            html: `<!-- ${name} -->\n`, xml: `<!-- ${name} -->\n`, svg: `<!-- ${name} -->\n`,
            py: `# ${name}\n`, rb: `# ${name}\n`, sh: `# ${name}\n`, bash: `# ${name}\n`,
            yaml: `# ${name}\n`, yml: `# ${name}\n`, toml: `# ${name}\n`,
            java: `// ${name}\n`, kt: `// ${name}\n`, swift: `// ${name}\n`,
            go: `// ${name}\n`, rs: `// ${name}\n`, c: `// ${name}\n`, cpp: `// ${name}\n`,
            php: `<?php\n// ${name}\n`, lua: `-- ${name}\n`, sql: `-- ${name}\n`,
            md: `# ${name.replace(/\.md$/i, '')}\n`,
        };
        const fileContent = commentMap[ext] ?? '';
        return postWorkspace(`/workspaces/${workspaceId}/files/save`, { filePath, fileContent, commitMessage: `[Human Action] Created file: ${filePath}` });
    },
    readFileContent: (workspaceId, filePath) => postWorkspace(`/workspaces/${workspaceId}/files/read`, { filePath }),
    deleteFile: (workspaceId, filePath) => postWorkspace(`/workspaces/${workspaceId}/files/delete`, { filePath }),
    renameFile: (workspaceId, oldPath, newPath) => postWorkspace(`/workspaces/${workspaceId}/files/rename`, { oldPath, newPath }),
    saveFileContent: (workspaceId, filePath, fileContent, commitMessage) => postWorkspace(`/workspaces/${workspaceId}/files/save`, { filePath, fileContent, commitMessage: commitMessage || `[Human Edit] Updated ${filePath}` }),
    saveMultipleFiles: (workspaceId, files, commitMessage) => postWorkspace(`/workspaces/${workspaceId}/files/save-batch`, { files, commitMessage: commitMessage || `[Human Batch Add] Added ${files.length} files` }),
    findAndReplace: (workspaceId, findString, replaceString, filesGlob) => postWorkspace(`/workspaces/${workspaceId}/files/find-and-replace`, { findString, replaceString, filesGlob }),

    executeCommand: (...args) => {
        const workspaceId = args.length === 3 ? args[1] : args[0];
        const command = args.length === 3 ? args[2] : args[1];
        return postWorkspace(`/workspaces/${workspaceId}/execute-command`, { command });
    },

    createTerminalV2Session: (workspaceId, options = {}) => {
        return postWorkspace(`/terminal-v2/workspaces/${workspaceId}/sessions`, options);
    },

    getTerminalV2Session: (sessionId) => {
        return getWorkspace(`/terminal-v2/sessions/${sessionId}`);
    },

    subscribeToTerminalV2Session: async (sessionId, onEvent, signal) => {
        const idToken = await getIdToken();
        const response = await fetch(`${CHAT_API_URL}/terminal-v2/sessions/${sessionId}/stream`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${idToken}`,
                Accept: 'text/event-stream',
            },
            signal,
        });

        if (!response.ok) {
            await handleResponse(response);
        }

        return streamSseResponse(response, onEvent, signal);
    },

    sendTerminalV2Input: (sessionId, data) => {
        return postWorkspace(`/terminal-v2/sessions/${sessionId}/input`, { data });
    },

    resizeTerminalV2Session: (sessionId, cols, rows) => {
        return postWorkspace(`/terminal-v2/sessions/${sessionId}/resize`, { cols, rows });
    },

    signalTerminalV2Session: (sessionId, signalName) => {
        return postWorkspace(`/terminal-v2/sessions/${sessionId}/signal`, { signal: signalName });
    },

    requestTerminalV2Export: (sessionId) => {
        return postWorkspace(`/terminal-v2/sessions/${sessionId}/export`, {});
    },

    approveTerminalV2PendingAction: (sessionId) => {
        return postWorkspace(`/terminal-v2/sessions/${sessionId}/approve`, {});
    },

    denyTerminalV2PendingAction: (sessionId) => {
        return postWorkspace(`/terminal-v2/sessions/${sessionId}/deny`, {});
    },

    killTerminalV2Session: (sessionId) => {
        return postWorkspace(`/terminal-v2/sessions/${sessionId}/kill`, {});
    },

    listMyGithubRepos: () => postWorkspace('/github/repos', {}),
    cloneRepository: (repoUrl, workspaceName = null) => postWorkspace('/github/clone-repo', {
        repoUrl,
        ...(workspaceName ? { workspaceName } : {}),
    }),
    cloneIntoWorkspace: (workspaceId, repoUrl) => postWorkspace(`/workspaces/${workspaceId}/github/clone-into`, { repoUrl }),
    createRepoFromWorkspace: (workspaceId, repoName, isPrivate = true) => postWorkspace(`/workspaces/${workspaceId}/github/create-repo`, { repoName, isPrivate }),
    pushToGithub: (workspaceId, commitMessage, branch = 'main') => postWorkspace(`/workspaces/${workspaceId}/github/push`, { commitMessage, branch }),
    hardPullFromRemote: (workspaceId, branch = 'main') => postWorkspace(`/workspaces/${workspaceId}/github/pull`, { branch }),
    checkRemoteStatus: (workspaceId) => postWorkspace(`/workspaces/${workspaceId}/github/check-remote`, {}),

    downloadWorkspaceZip: async (workspaceId) => {
        const token = await getIdToken();
        const response = await fetch(`${CHAT_API_URL}/workspaces/${workspaceId}/zip`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
            await handleResponse(response);
        }

        const blob = await response.blob();
        const fallbackName = `${workspaceId}.zip`;
        const downloadFileName = getWorkspaceFilenameFromDisposition(response.headers.get('Content-Disposition'), fallbackName);

        return {
            downloadUrl: window.URL.createObjectURL(blob),
            downloadFileName,
        };
    },

    uploadAndUnzip: async (workspaceId, file) => {
        const token = await getIdToken();
        const formData = new FormData();
        formData.append('zipfile', file);
        const response = await fetch(`${CHAT_API_URL}/workspaces/${workspaceId}/files/upload-zip`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        return handleResponse(response);
    },

    downloadFile: async (workspaceId, filePath, fileName) => {
        const res = await postWorkspace(`/workspaces/${workspaceId}/files/read`, { filePath });
        const blob = new Blob([res.content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    },

    cancelAndRevertPhase: (workspaceId, phaseId) => postWorkspace(`/workspaces/${workspaceId}/phases/${phaseId}/cancel`, {}),
    restartPhase: (workspaceId, phaseId) => postWorkspace(`/workspaces/${workspaceId}/phases/${phaseId}/restart`, {}),
    undoPhase: (workspaceId, phaseId) => postWorkspace(`/workspaces/${workspaceId}/phases/${phaseId}/undo`, {}),
    revertToPoint: (workspaceId, commitHash, phaseId) => postWorkspace(`/workspaces/${workspaceId}/revert`, { commitHash, fromPhaseId: phaseId }),

    updateAccountKeys: (keys) => postWorkspace('/account/keys/update', { keys }),
    deleteApiKey: (keyName) => postWorkspace('/account/keys/delete', { keyName }),

    getWorkspaceContextForPrompt: async (workspaceId) => {
        const normalizedWorkspaceId = normalizeWorkspaceContextId(workspaceId);

        try {
            const detailsPromise = apiManager.listAllWorkspacesWithDetails();
            const filesPromise = apiManager.listAllFiles(normalizedWorkspaceId);
            const [detailsResult, filesResult] = await Promise.all([detailsPromise, filesPromise]);

            const workspaceDetails = detailsResult.workspaces?.find((workspace) => workspace.id === normalizedWorkspaceId);
            const summary = typeof workspaceDetails?.summary === 'string' && workspaceDetails.summary.trim()
                ? workspaceDetails.summary.trim()
                : null;

            const formatFileTree = (files, depth = 3) => {
                if (!Array.isArray(files)) return '(Could not parse file tree)';
                const treeLines = ['project-root/'];
                const sortedFiles = [...files].sort((left, right) => left.path.localeCompare(right.path));

                for (const file of sortedFiles) {
                    const parts = file.path.split('/');
                    if (parts.length > depth) continue;
                    const prefix = parts.length > 1 ? '│   '.repeat(parts.length - 1) : '';
                    const suffix = file.type === 'directory' ? '/' : '';
                    treeLines.push(`${prefix}├── ${parts[parts.length - 1]}${suffix}`);
                }

                if (sortedFiles.some((file) => file.path.split('/').length > depth)) {
                    treeLines.push('└── ... (more files)');
                }
                return treeLines.join('\n');
            };

            return {
                summary,
                fileTree: formatFileTree(filesResult.files),
            };
        } catch (error) {
            console.error(`[apiManager] Failed to get workspace context for ${normalizedWorkspaceId}`, error);
            return { summary: null, fileTree: null };
        }
    },
};